import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  concatMap,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { CanvasElementId } from '@blaze-runtime/engine-domain';
import { getActiveBinding } from '@blaze-shared/elements-domain';
import { sentryLog } from '@blaze-shared/logger-util';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { BlazeSchema, findColumn } from '@blaze-shared/schema-domain';
import { countedShareReplay, isDeepEqual, isNullishOrEmptyString } from '@blaze-shared/shared-util';

import { RuntimeFacade } from '../../../../+state/runtime/runtime.facade';
import { FormListRowsService } from '../../../../elements/form-list/form-list-rows.service';
import { ElementDefinitionsService } from '../../../../services/element-definitions.service';
import { ElementStoreViewService } from '../../../../services/element-store-view.service';
import {
  getFormListDataRowById,
  getFormListIdColumn,
} from '../../../../utils/elements/form-list.utils';
import { hasDataProps } from '../../../element-model/element-props-guards';
import { DataProviderQuery, InputDataPackage } from '../../io-data.models';

import { DataSourceProvider } from './data-source.data-provider';

type FormListBasePayload = {
  container: 'formList';
  idColumn: string;
  schema: BlazeSchema;
  data: any;
};

const NO_DATA_MATCH_TOKEN = { no_data: true };

@Injectable({ providedIn: 'root' })
export class FormListDataProvider implements DataSourceProvider {
  private readonly elementDefinitionsService = inject(ElementDefinitionsService);
  private readonly formListRowsService = inject(FormListRowsService);
  private readonly elementStoreView = inject(ElementStoreViewService);
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);
  private readonly containersProvidersMap = new Map<string, Observable<FormListBasePayload>>();
  readonly sourceType = 'editableList' as const;

  cleanupBaseProviders(containerId: CanvasElementId): void {
    this.containersProvidersMap.delete(containerId);
  }

  getDataStream({
    source,
    element,
    bindings,
    rowContext,
  }: DataProviderQuery): Observable<InputDataPackage> {
    const dataSourceId = source.id;
    if (!bindings) {
      throw new Error('Form List: binding missing');
    }
    if (rowContext === undefined) {
      throw new Error('Form List: rowContext missing');
    }
    if (!element) {
      throw new Error('Form List: element missing');
    }

    const columnId = Object.values(bindings)[0];
    const field = this.schemaService.getColumnName(columnId) ?? '';

    return this.getProviderBase(dataSourceId).pipe(
      filter(({ data, schema }) => {
        return !!data && !!schema && (!data.length || !isNullishOrEmptyString(rowContext.rowId));
      }),
      map(({ idColumn, schema, data }) => {
        const rows = Array.isArray(data) ? data : data?.rows;
        const emptyishRows = this.formListRowsService.getFormListEmptyishRows(dataSourceId);
        const { dataRow } =
          getFormListDataRowById(rows, emptyishRows, idColumn, rowContext.rowId) ?? {};
        if (!dataRow) {
          return { data: NO_DATA_MATCH_TOKEN };
        }
        return {
          data: dataRow[field] as unknown,
          schema,
          rowContext,
        };
      }),
      filter(({ data }) => data !== NO_DATA_MATCH_TOKEN),
      distinctUntilChanged(isDeepEqual),
      map(({ data, schema, rowContext }) => {
        return <InputDataPackage>{
          sourceType: 'editableList',
          data,
          schema,
          column: schema ? findColumn(schema.columns, columnId) : undefined,
          rowContext,
        };
      }),
    );
  }

  private getProviderBase(dataSourceId: string): Observable<FormListBasePayload> {
    if (this.containersProvidersMap.has(dataSourceId)) {
      return this.containersProvidersMap.get(dataSourceId) as Observable<FormListBasePayload>;
    }

    const formListDefaultOutput =
      this.elementDefinitionsService.getDefaultOutputEventType('editableList');
    const formListOutput$ = this.store
      .select(runtimeSelectors.selectOutputUiById(dataSourceId, formListDefaultOutput))
      .pipe(
        filter((output, index) => index === 0 || !output?.updateSource?.startsWith('form-input')),
        map((output) => output?.data),
        countedShareReplay(),
      );

    const providerBase$: Observable<FormListBasePayload> = this.elementStoreView
      .getElement(dataSourceId)
      .pipe(
        distinctUntilChanged(isDeepEqual),
        switchMap((formElement) => {
          if (!hasDataProps(formElement)) {
            return of(undefined);
          }
          let formDataSource = getActiveBinding(formElement.props.data, 'value')?.dataSource;
          if (!formDataSource) {
            return of(undefined);
          }

          if (formDataSource.sourceType === 'mirror') {
            const targetElement = this.runtimeFacade.elementsValue[formDataSource.id];
            if (!targetElement || !hasDataProps(targetElement)) {
              throw new Error('target element missing or invalid');
            }
            formDataSource = getActiveBinding(targetElement.props.data, 'value')?.dataSource;
            if (!formDataSource) {
              throw new Error('target element datasource missing or invalid');
            }
          }

          return formDataSource.sourceType === 'dataset'
            ? this.schemaService.obtainSchema(formDataSource.id)
            : this.store.select(runtimeSelectors.selectWorkflowsSchemaById(formDataSource.id));
        }),
        distinctUntilChanged(isDeepEqual),
        concatMap((schema) => {
          const idColumn = getFormListIdColumn(schema);

          if (!schema || !idColumn) {
            console.error('Form List input source schema missing', schema);
            sentryLog('Form List input source schema missing', 'error');
            return EMPTY;
          }

          return formListOutput$.pipe(
            map((data) => ({
              container: 'formList' as const,
              schema,
              idColumn,
              data,
            })),
          );
        }),
        countedShareReplay(),
      );

    this.containersProvidersMap.set(dataSourceId, providerBase$);
    return providerBase$;
  }
}
